/**
 * Response normalization & error parsing for work orders (pure functions plus ctx-bound normalizers).
 *
 * Extracted verbatim from workOrdersService.ts — functions are invoked with
 * `this` bound to the WorkOrdersService instance (WorkOrdersCtx).
 */
import { extractItems } from '../../utils/apiHelpers';
export function normalizeVehicle(v) {
    const src = v || {};
    const vehicle = {
        vin: src.vin,
        make: src.make,
        model: src.model,
        year: src.year,
        color: src.color,
        mileage: src.mileage,
        stock_number: src.stock_number || src.stock,
        thumbnail_url: src.thumbnail_url,
    };
    const hasAny = vehicle.vin ||
        vehicle.make ||
        vehicle.model ||
        vehicle.year ||
        vehicle.color ||
        vehicle.mileage ||
        vehicle.stock_number ||
        vehicle.thumbnail_url;
    return hasAny ? vehicle : undefined;
}
export function normalizeTag(input) {
    if (!input)
        return null;
    if (typeof input === 'string')
        return { id: input, name: input };
    const id = input.id || input.tag_id || input.uuid || input.key;
    if (!id)
        return null;
    const name = input.name || input.label || input.title || id;
    return {
        id,
        name,
        color: input.color ?? input.hex_color ?? input.hex ?? input.color_code ?? null,
        description: input.description ?? input.details ?? null,
    };
}
export function normalizeTags(value) {
    const rawList = Array.isArray(value)
        ? value
        : Array.isArray(value?.items)
            ? value.items
            : Array.isArray(value?.data)
                ? value.data
                : undefined;
    if (!rawList)
        return undefined;
    const tags = rawList
        .map((raw) => this.normalizeTag(raw))
        .filter((tag) => Boolean(tag));
    return tags.length ? tags : [];
}
export function normalizeWorkOrder(o) {
    // Build vehicle data by merging vehicle_info (or vehicle) with
    // vehicle_snapshot, then override with top-level fields ONLY when they
    // are truthy — an empty-string/0/null top-level vin/make/model/year must
    // not clobber good data from vehicle_info (web semantics, kept for both
    // platforms).
    // IMPORTANT: the top-level thumbnail_url is the work-order image, not the
    // vehicle thumbnail, so it is deliberately NOT merged into the vehicle.
    const vehicleData = {
        ...(o.vehicle_info || o.vehicle || {}),
        ...(o.vehicle_snapshot || {}),
        ...(o.vin ? { vin: o.vin } : {}),
        ...(o.make ? { make: o.make } : {}),
        ...(o.model ? { model: o.model } : {}),
        ...(o.year ? { year: o.year } : {}),
    };
    const vehicle = this.normalizeVehicle(Object.keys(vehicleData).length > 0 ? vehicleData : undefined);
    const current_department_id = o.current_department_id ?? o.active_department ?? o.department_id ?? o.current_department?.id ?? o.department?.id ?? null;
    const current_department_name = o.current_department_name ?? o.department_name ?? o.current_department?.name ?? o.department?.name ?? undefined;
    const current_department = current_department_id || current_department_name ? { id: current_department_id, name: current_department_name } : null;
    const tags = this.normalizeTags(o.tags || o.work_order_tags || o.workorder_tags || o.tag_ids);
    const summary = {
        id: o.id,
        work_order_display_id: o.work_order_display_id ?? undefined,
        location_id: o.location_id,
        name: o.name ?? null,
        vehicle,
        active_department_assigned_users: o.active_department_assigned_users ?? null,
        status: o.status || o.state || 'open',
        status_updated_at: o.status_updated_at || o.updated_at || null,
        current_department_id,
        current_department,
        active_department_id: o.active_department_id ?? null,
        department_name: o.department_name ?? null,
        department_section_id: o.department_section_id ?? null,
        department_section_name: o.department_section_name ?? null,
        department_entry_time: o.department_entry_time ?? null,
        past_due: o.past_due ?? null,
        invoice: o.invoice ?? null,
        // The advisor (work_orders.created_by, resolved server-side). This is an
        // allowlist, not a spread, so a field missing from it is silently dropped
        // however correct the API response is — which is exactly what happened
        // here: the header rendered "Advisor —" on every work order.
        advisor_name: o.advisor_name ?? null,
        workflow_id: o.workflow_id || null,
        workflow_name: o.workflow_name || null,
        priority: o.priority || 0,
        created_at: o.created_at,
        updated_at: o.updated_at,
        notes_count: o.notes_count || 0,
        images_count: o.images_count || 0,
        ro_number: o.ro_number ?? null,
        hat_number: o.hat_number ?? null,
        po_number: o.po_number ?? null,
        notes: o.notes ?? null,
        deleted_at: o.deleted_at ?? null,
        is_deleted: o.is_deleted ?? false,
        thumbnail_key: o.thumbnail_key ?? null,
        thumbnail_url: o.thumbnail_url ?? null,
        work_order_type: o.work_order_type ?? undefined,
        original_work_order_id: o.original_work_order_id ?? undefined,
        requires_reapproval: o.requires_reapproval ?? false,
        reapproval_note: o.reapproval_note ?? null,
        reapproval_requested_at: o.reapproval_requested_at ?? null,
    };
    if (tags)
        summary.tags = tags;
    return summary;
}
/**
 * Normalize a list endpoint payload into ListWorkOrdersResponse, tolerating
 * both bare-array and envelope shapes, and deriving has_more when the
 * backend omits it (web semantics).
 */
export function buildListResponse(data, opts) {
    const list = extractItems(data, ['work_orders', 'workorders', 'workOrders', 'results']);
    const work_orders = list.map((wo) => this.normalizeWorkOrder(wo));
    const isArray = Array.isArray(data);
    return {
        work_orders,
        total: Number(isArray ? work_orders.length : (data?.total ?? work_orders.length ?? 0)),
        limit: Number(isArray ? (opts?.limit ?? work_orders.length ?? 0) : (data?.limit ?? opts?.limit ?? work_orders.length ?? 0)),
        offset: Number(isArray ? (opts?.offset ?? 0) : (data?.offset ?? opts?.offset ?? 0)),
        has_more: Boolean(isArray ? false : (data?.has_more ?? (data?.count ? data.count + (data.offset ?? 0) < (data.total ?? 0) : false))),
        count: Number(isArray ? work_orders.length : (data?.count ?? work_orders.length ?? 0)),
    };
}
/**
 * Parse a reopenWorkOrder failure into its structured parts. The backend
 * returns {error, code, caller_can_override} — callers use the code to branch
 * (REOPEN_WINDOW_EXPIRED vs REOPEN_ACCOUNTING_BLOCKED) and caller_can_override
 * to decide whether to offer the acknowledge-impacts retry.
 *
 * Checks the structured errorBody first (reopenWorkOrder preserves it), then
 * falls back to a JSON-encoded message like parseCreditError does. The
 * fallback alone is not enough: the ApiClient sets `error` to the body's
 * prose `error` field, so JSON.parse on the message always throws and the
 * code/caller_can_override are lost.
 */
export function parseReopenError(e) {
    const raw = e instanceof Error ? e.message : 'Unknown error';
    const body = e?.errorBody;
    if (body && typeof body === 'object') {
        const { error, code, caller_can_override, detail, invoice_ids } = body;
        if (typeof error === 'string' || typeof code === 'string') {
            return {
                message: typeof error === 'string' && error ? error : raw,
                code: typeof code === 'string' ? code : undefined,
                callerCanOverride: caller_can_override === true,
                detail: typeof detail === 'string' ? detail : undefined,
                invoiceIds: Array.isArray(invoice_ids) ? invoice_ids.filter((v) => typeof v === 'string') : undefined,
            };
        }
    }
    try {
        const parsed = JSON.parse(raw);
        return {
            message: parsed.error || raw,
            code: parsed.code,
            callerCanOverride: parsed.caller_can_override === true,
            detail: typeof parsed.detail === 'string' ? parsed.detail : undefined,
            invoiceIds: Array.isArray(parsed.invoice_ids)
                ? parsed.invoice_ids.filter((v) => typeof v === 'string')
                : undefined,
        };
    }
    catch {
        return { message: raw };
    }
}
